import { AsyncPipe, NgClass } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  ViewRef,
} from '@angular/core';
import { Product, ProductService, RoutingService, TranslatePipe } from '@spartacus/core';
import { CmsComponentData, MediaComponent } from '@spartacus/storefront';
import { DeviceDetectorService } from 'ngx-device-detector';
import { SlickCarouselModule } from 'ngx-slick-carousel';
import { EMPTY, Observable, Subscription, combineLatest, concat, forkJoin, of } from 'rxjs';
import { filter, first, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { CheckoutConstants } from 'src/app/model/checkout.model';
import { DealerPrice } from 'src/app/model/dealer-price.model';
import { ShimanoProduct } from 'src/app/model/product.model';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { DealerPriceService } from 'src/app/service/dealer-price/dealer-price.service';
import {
  GoogleTagManagerEvent,
  GoogleTagManagerService,
} from 'src/app/service/googletagmanager/google-tag-manager.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { CAROUSEL_HIGHLIGHTED_PRODUCTS, TooltipIconService } from 'src/app/service/tooltip-icon/tooltip-icon.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';
import { RemoveImageTitleAttrDirective } from '../../../shared/remove-title-attr.directive';
import { ShimanoUtilMethod } from '../../../utility/methods.util';
import { SlickCarouselUtilService } from '../../../utility/slick-carousel.util';
import { ShimanoCmsProductCarouselComponent } from '../product-carousel/product-carousel.component';
import { ErpPromotionalTextComponent } from '../promotional-text/erp-promotional-text/erp-promotional-text.component';
import { PromotionalTextComponent } from '../promotional-text/promotional-text.component';
import { POINTER_EVENTS_NONE_CLASS, TooltipIconComponent } from '../tooltip-icon/tooltip-icon.component';

@Component({
  selector: 'app-highlighted-product',
  templateUrl: './highlighted-product.component.html',
  styleUrls: ['./highlighted-product.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgClass,
    SlickCarouselModule,
    MediaComponent,
    RemoveImageTitleAttrDirective,
    TooltipIconComponent,
    ErpPromotionalTextComponent,
    PromotionalTextComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class HighlightedProductComponent implements OnInit, OnDestroy, AfterViewChecked {
  private componentData$: Observable<ShimanoCmsProductCarouselComponent> = this.componentData.data$.pipe(
    filter(Boolean)
  );
  normal = true;
  showSrp = false;
  srpSubscription$: Subscription;
  dealerPrices: DealerPrice[];
  size: any;
  carouselLocation: string;
  itemsSubscription$: Subscription;
  hasDealerPrice = true;
  dealerPriceCheckerSubscription$: Subscription;
  atLeastOneHasHybrisPromo = false;
  atLeastOneHasErpPromo = false;

  gtmbusinessType: string = this.currentBusinesstype.getBusinessType();
  affiliation = `/${this.gtmbusinessType}/${this.gtmActiveCountry}/${this.activeLanguage}`;

  /**
   * returns an Obervable string for the title.
   */
  title$: Observable<string> = this.componentData$.pipe(map((data) => data.title));

  /**
   * Obervable that holds an Array of Observables. This is done, so that
   * the component UI could consider to lazy load the UI components when they're
   * in the viewpoint.
   */
  items$: Observable<Observable<ShimanoProduct>[]> = this.componentData$.pipe(
    first(),
    tap((data) => {
      this.carouselLocation = data.name;
    }),
    map((data) => data.productCodes && data.productCodes.trim().split(' ').splice(0, data.itemCount)),
    mergeMap((productCodes) => {
      return forkJoin([
        of(productCodes),
        productCodes && productCodes.length ? this.getDealerPrices(productCodes) : EMPTY,
      ]);
    }),
    map(([codes, dealerPrices]) => {
      this.dealerPrices = dealerPrices.dealerPrices;
      return codes.map((code) => this.productService.get(code));
    })
  ) as Observable<Observable<ShimanoProduct>[]>;

  slickConfig: any;
  @ViewChild('prodImageElement', { static: false }) prodImageElement: ElementRef;

  activeCountry$: Observable<string> = this.countryService.getActive();
  activeLanguage$: Observable<string> = this.languageService.getActive();

  noPointerEventsOnElement: string;
  tooltipIconConfig$: Observable<any> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.tooltipIconService.getTooltipIconConfig(activeCountry, CAROUSEL_HIGHLIGHTED_PRODUCTS)
    ),
    tap((tooltipInfoConfig) => (this.noPointerEventsOnElement = tooltipInfoConfig.noPointerEventsOnElement))
  );

  showGstExclusive$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_EXCLUSIVE)
    )
  );

  showGstInclusive$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_INCLUSIVE)
    )
  );
  businessType: string;

  constructor(
    protected componentData: CmsComponentData<any>,
    protected productService: ProductService,
    private deviceService: DeviceDetectorService,
    private componentService: ComponentService,
    private dealerPriceService: DealerPriceService,
    private cdr: ChangeDetectorRef,
    private gtmService: GoogleTagManagerService,
    private countryService: ShimanoCountryService,
    private tooltipIconService: TooltipIconService,
    private routingService: RoutingService,
    private checkoutServiceExtends: CheckoutServiceExtends,
    private currentBusinesstype: CurrentBusinessType,
    private languageService: ShimanoLanguageService,
    private storageService: StorageService
  ) {
    const slickConfig = new SlickCarouselUtilService();
    const slickObj = slickConfig.defaultConfig;
    slickObj.slidesToShow = 4;
    slickObj.slidesToScroll = 4;
    if (!this.deviceService.isDesktop()) {
      slickObj.arrows = false;
    }
    this.slickConfig = slickObj;
  }

  ngOnInit() {
    this.itemsSubscription$ = this.items$
      .pipe(
        map((items$) => {
          combineLatest(items$.map((item) => concat(item))).subscribe((products) => {
            this.gtmHighlightedProductImpressions(products, products.length);
          });
        })
      )
      .subscribe();

    this.getInitSrpToggleValue();

    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrp = checked;
      this.cdr.detectChanges();
    });

    this.dealerPriceCheckerSubscription$ = this.componentService.hasDealerPrice$.subscribe((hasDealerPrice) => {
      this.hasDealerPrice = hasDealerPrice;
      if (!(this.cdr as ViewRef).destroyed) {
        this.cdr.detectChanges();
      }
    });
    this.businessType = this.currentBusinesstype.getBusinessType();
  }

  get gtmActiveCountry(): string {
    let activeCountry: string;
    this.countryService.getActive().subscribe((country) => (activeCountry = country));
    return activeCountry;
  }

  get activeLanguage(): string {
    let activeLanguage: string;
    this.languageService.getActive().subscribe((lang) => (activeLanguage = lang));
    return activeLanguage;
  }

  ngAfterViewChecked() {
    if (this.prodImageElement && this.deviceService.isDesktop()) {
      if (this.prodImageElement.nativeElement.offsetWidth > 0) {
        this.size = this.prodImageElement.nativeElement.offsetWidth;
        this.cdr.detectChanges();
      }
    }
  }

  getInitSrpToggleValue() {
    this.showSrp = this.storageService.getLocalStorageItem('showSrp') === 'true';
  }

  getImageRendition(image: any) {
    let size: string;
    if (this.deviceService.isDesktop()) {
      size = ShimanoUtilMethod.imageRendition(295);
    } else {
      size = ShimanoUtilMethod.imageRendition(177);
    }
    return ShimanoUtilMethod.newImageFormat(image, size);
  }

  getDealerPrices(productCodes: string[]): Observable<any> {
    return this.dealerPriceService.getDealerPricesByProductCodes(productCodes);
  }

  dealerPrice(productCode: string): string {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceByProductCode(this.dealerPrices, productCode);
    }
  }

  dealerPriceData(productCode: string): DealerPrice {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceData(this.dealerPrices, productCode);
    }
  }

  dealerPriceValue(productCode: string): number {
    if (this.dealerPrices) {
      return this.dealerPriceService.getDealerPriceValueByProductCode(this.dealerPrices, productCode);
    }
  }

  goToPdp(product: Product, event: any, position: number) {
    if (!this.hasPointerEventsNone(event.target)) {
      this.routingService.go({
        cxRoute: 'product',
        params: product,
      });
      this.gtmProductClick(product, position);
    }
  }

  hasPointerEventsNone(element: Element): boolean {
    const closestElement = element.closest(this.noPointerEventsOnElement);
    return closestElement && closestElement.classList.contains(POINTER_EVENTS_NONE_CLASS);
  }

  erpPromoValue(productCode: string): string {
    if (this.dealerPrices) {
      return this.dealerPriceService.getErpPromoByProductCode(this.dealerPrices, productCode);
    }
  }

  gtmHighlightedProductImpressions(products: any[], count: number) {
    const impressionsList: any[] = [];
    const promotionsList: any[] = [];
    products.forEach((product, index) => {
      if (product) {
        impressionsList.push({
          item_id: product?.code,
          item_name: product?.name,
          affiliation: this.affiliation,
          index: index,
          item_brand: product?.brand?.name,
          price: this.dealerPriceValue(product?.code),
          item_list_name: this.carouselLocation,
        });
        promotionsList.push(
          product.potentialPromotions && product.potentialPromotions.length ? product.potentialPromotions[0] : null
        );
      }
    });

    const gtmTag = {
      event: GoogleTagManagerEvent.PRODUCT_IMPRESSION,
      ecommerce: {
        currency: this.storageService.getSessionStorageItem('currency'),
        items: impressionsList,
      },
    };
    if (count === gtmTag.ecommerce.items.length) {
      this.gtmService.pushTag({
        ecommerce: null,
        search_term: '',
        results_page: '',
        affiliation: '',
        content_type: '',
        content_id: '',
        content_name: '',
      });
      this.gtmService.pushTag(gtmTag);
      this.gtmPromoView(products);

      products.forEach((product) => {
        if (
          !this.atLeastOneHasHybrisPromo &&
          product.potentialPromotions &&
          product.potentialPromotions[0].description
        ) {
          this.atLeastOneHasHybrisPromo = true;
        }
        if (!this.atLeastOneHasErpPromo && this.erpPromoValue(product.code)) {
          this.atLeastOneHasErpPromo = true;
        }
      });
    }
  }

  gtmProductClick(product: Product, position: number) {
    const gtmTag = {
      event: GoogleTagManagerEvent.PRODUCT_CLICK,
      ecommerce: '',
      content_type: 'product',
      content_id: product.code,
      content_name: product.name,
    };
    this.gtmService.pushTag({
      ecommerce: null,
      search_term: '',
      results_page: '',
      affiliation: '',
      content_type: '',
      content_id: '',
      content_name: '',
    });
    this.gtmService.pushTag(gtmTag);
  }

  gtmPromoView(products: any) {
    products.forEach((product: any, index: any) => {
      if (product) {
        if (product.potentialPromotions && product.potentialPromotions.length) {
          this.gtmService.pushTag({
            ecommerce: null,
            search_term: '',
            results_page: '',
            affiliation: '',
            content_type: '',
            content_id: '',
            content_name: '',
          });
          this.gtmService.pushTag({
            event: GoogleTagManagerEvent.PROMO_VIEW,
            ecommerce: {
              promotion_id: product.potentialPromotions[0].code,
              promotion_name: product.potentialPromotions[0].description
                ? product.potentialPromotions[0].description
                : '',
              creative_slot: this.carouselLocation,
              items: [
                {
                  item_id: product?.code,
                  item_name: product?.name,
                  affiliation: this.affiliation,
                  index: index,
                  item_brand: product?.brand?.name,
                  price: product?.basePrice?.formattedValue,
                },
              ],
            },
          });
        }
      }
    });
  }

  ngOnDestroy() {
    if (this.srpSubscription$) {
      this.srpSubscription$.unsubscribe();
    }
    if (this.itemsSubscription$) {
      this.itemsSubscription$.unsubscribe();
    }
    if (this.dealerPriceCheckerSubscription$) {
      this.dealerPriceCheckerSubscription$.unsubscribe();
    }
  }
}
